import { useMemo, useState } from "react";

import { type Plugin, type ChartDataset } from "chart.js";
import moment from "moment";
import { Line } from "react-chartjs-2";
import { twMerge } from "tailwind-merge";

import { useUser } from "@components/user-context";
import { drawAlternativeMetricTitle } from "@features/sales-v3/utils/canvas";
import { buildLegend } from "@features/sales-v3/utils/tooltip";
import { type FullBudgetMixResponse } from "@north-beam/nb-common";
import { Checkbox } from "@shared/checkbox";

import { type Kpi } from "../../client-setting/types";

import { getChartOptions } from "./chart-options";

type Props = {
  selectedBudgetMix: FullBudgetMixResponse;
  selectedKpi: Kpi;
  height?: number;
  legendContainerClassname?: string;
  useDefaultTooltip?: boolean;
  isBudgetPacingGraph?: boolean;
};

const SPEND_ID = "SPEND";
const KPI_RANGE_ID = "RANGE";
const KPI_MEDIAN_ID = "MEDIAN";

export const ForecastPacingChart = ({
  selectedBudgetMix,
  selectedKpi,
  height,
  legendContainerClassname,
  useDefaultTooltip = false,
  isBudgetPacingGraph = false,
}: Props) => {
  const { user } = useUser();

  const [datasetVisibility, setDatasetVisibility] = useState<
    Record<string, boolean>
  >({
    [SPEND_ID]: true,
    [KPI_RANGE_ID]: true,
    [KPI_MEDIAN_ID]: true,
  });

  const toggleDatasetVisibility =
    (datasetId: keyof typeof datasetVisibility) => (checked: boolean) =>
      setDatasetVisibility((prev: typeof datasetVisibility) => ({
        ...prev,
        [datasetId]: checked,
      }));

  const chartData = useMemo(() => {
    const labels = selectedBudgetMix.data.weekly_data.map(({ start_date }) =>
      moment(start_date).format("MMM DD"),
    );

    const datasets: (ChartDataset<"line", number[]> & { id: string })[] = [
      {
        id: SPEND_ID,
        data: selectedBudgetMix.data.weekly_data.map(({ spend_data }) =>
          spend_data.reduce((acc, { spend }) => acc + spend, 0),
        ),
        label: "Spend",
        yAxisID: "y1",
        animation: false,
        pointRadius: 0,
        pointHitRadius: 50,
        hoverBackgroundColor: "#7081FF",
        borderColor: "#7081FF",
        hoverBorderColor: "#7081FF",
        borderDash: [6, 2],
        hidden: !datasetVisibility[SPEND_ID],
      },
      {
        id: KPI_MEDIAN_ID,
        data: selectedBudgetMix.data.weekly_data.map(
          ({ summary_data }) =>
            summary_data.kpi_data[selectedKpi.kpi_name].values.median,
        ),
        label: selectedKpi.friendly_name,
        yAxisID: "y",
        animation: false,
        pointRadius: 0,
        pointHitRadius: 50,
        hoverBackgroundColor: "#7081FF",
        borderColor: "#7081FF",
        hoverBorderColor: "#7081FF",
        hidden: !datasetVisibility[KPI_MEDIAN_ID],
      },
      {
        id: KPI_RANGE_ID,
        label: "Low",
        data: selectedBudgetMix.data.weekly_data.map(
          ({ summary_data }) =>
            summary_data.kpi_data[selectedKpi.kpi_name].values.low,
        ),
        backgroundColor: "#EAECFF",
        fill: "+1",
        yAxisID: "y",
        pointRadius: 0,
        borderWidth: 0,
        animation: false,
        hidden: !datasetVisibility[KPI_RANGE_ID],
      },
      {
        id: KPI_RANGE_ID,
        label: "High",
        data: selectedBudgetMix.data.weekly_data.map(
          ({ summary_data }) =>
            summary_data.kpi_data[selectedKpi.kpi_name].values.high,
        ),
        yAxisID: "y",
        pointRadius: 0,
        borderWidth: 0,
        animation: false,
        hidden: !datasetVisibility[KPI_RANGE_ID],
      },
    ];

    return { labels, datasets };
  }, [selectedKpi, selectedBudgetMix, datasetVisibility]);

  const chartOptions = useMemo(
    () =>
      getChartOptions({
        selectedKpi,
        user,
        selectedBudgetMix,
        useDefaultTooltip,
      }),
    [selectedKpi, user, selectedBudgetMix, useDefaultTooltip],
  );

  const plugins = useMemo<Plugin<"line">[]>(() => {
    const otherBases = isBudgetPacingGraph
      ? { scaleBackgroundColor: "#F8F8F9" }
      : {};
    return [
      {
        id: "forecastPacingChartYScaleTitles",
        afterDraw(chart) {
          const {
            ctx,
            chartArea,
            scales: { y1 },
          } = chart;
          drawAlternativeMetricTitle(
            ctx,
            selectedKpi.friendly_name.toUpperCase(),
            {
              font: {
                family: "Inter",
                size: 12,
                weight: "600",
                style: "normal",
                lineHeight: 16,
              },
              x: 8,
              tickWidth: 12,
              chart,
              chartArea,
              config: {
                baseBackgroundColor: "#7081FF",
                ...otherBases,
              },
            },
          );
          drawAlternativeMetricTitle(ctx, "SPEND", {
            font: {
              family: "Inter",
              size: 12,
              weight: "600",
              style: "normal",
              lineHeight: 16,
            },
            x: y1.right - 2,
            tickWidth: -12,
            chart,
            chartArea,
            config: {
              ...otherBases,
              baseBackgroundColor: "#7081FF",
              borderDash: [6, 2],
            },
          });
        },
      },
    ];
  }, [isBudgetPacingGraph, selectedKpi.friendly_name]);

  const kpiTypeName =
    selectedKpi.kpi_type === "price_value" ? "Revenue" : "Transactions";

  return (
    <div>
      <div>
        <Line
          key={selectedKpi.id}
          height={height ?? 340}
          options={chartOptions}
          data={chartData}
          plugins={plugins}
        />
      </div>
      <div
        className={twMerge(
          "flex justify-center text-xs space-x-8 mt-6",
          legendContainerClassname,
        )}
      >
        <label className="flex items-center space-x-2 cursor-pointer">
          <Checkbox
            className="mr-0.5"
            value={datasetVisibility[KPI_MEDIAN_ID]}
            onChange={toggleDatasetVisibility(KPI_MEDIAN_ID)}
          />
          <div
            dangerouslySetInnerHTML={{
              __html: buildLegend(
                {
                  borderColor: "#7081FF",
                  backgroundColor: "#7081FF",
                  borderDash: [],
                },
                false,
              ),
            }}
          />
          <div>Median {kpiTypeName.toLowerCase()}</div>
        </label>
        <label className="flex items-center space-x-2 cursor-pointer">
          <Checkbox
            className="mr-0.5"
            value={datasetVisibility[SPEND_ID]}
            onChange={toggleDatasetVisibility(SPEND_ID)}
          />
          <div
            dangerouslySetInnerHTML={{
              __html: buildLegend(
                {
                  borderColor: "#7081FF",
                  backgroundColor: "#7081FF",
                  borderDash: [6, 2],
                },
                false,
              ),
            }}
          />
          <div>Spend</div>
        </label>
        <label className="flex items-center space-x-2 cursor-pointer">
          <Checkbox
            className="mr-0.5"
            value={datasetVisibility[KPI_RANGE_ID]}
            onChange={toggleDatasetVisibility(KPI_RANGE_ID)}
          />
          <div
            style={{
              backgroundColor: "#EAECFF",
              height: 15,
              width: 30,
            }}
          />
          <div>{kpiTypeName} range</div>
        </label>
      </div>
    </div>
  );
};
